import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  CheckCircle, 
  AlertCircle, 
  Loader2,
  Home,
  FileText,
  RefreshCw
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function PHSPaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get('session_id');
  const invoiceNumber = searchParams.get('invoice');
  
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pollCount, setPollCount] = useState(0);

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus();
    } else {
      setError('Invalid payment session');
      setLoading(false);
    }
  }, [sessionId]);

  const pollPaymentStatus = async () => {
    const maxAttempts = 5;
    const pollInterval = 2000; // 2 seconds

    if (pollCount >= maxAttempts) {
      setError('Payment status check timed out. Please check your email for confirmation.');
      setLoading(false);
      return;
    }

    try {
      const res = await axios.get(`${API}/api/phs/members/public/payment/status/${sessionId}`);
      setStatus(res.data);

      if (res.data.payment_status === 'paid') {
        setLoading(false);
      } else if (res.data.status === 'expired') {
        setError('Payment session expired. Please try again.');
        setLoading(false);
      } else {
        // Continue polling
        setPollCount(prev => prev + 1);
        setTimeout(pollPaymentStatus, pollInterval);
      }
    } catch (err) {
      console.error('Error checking payment status:', err);
      setError('Error checking payment status. Please try again.');
      setLoading(false);
    }
  };

  const handleRetry = () => {
    setLoading(true);
    setError(null);
    setPollCount(0);
    pollPaymentStatus();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 to-gray-100 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-8 text-center">
            <Loader2 className="h-16 w-16 mx-auto text-[#0F766E] mb-4 animate-spin" />
            <h2 className="text-xl font-bold text-gray-900 mb-2">Processing Payment</h2>
            <p className="text-gray-600">Please wait while we confirm your payment...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 to-gray-100 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-8 text-center">
            <AlertCircle className="h-16 w-16 mx-auto text-amber-500 mb-4" />
            <h2 className="text-xl font-bold text-gray-900 mb-2">Payment Status Unknown</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <div className="space-y-3">
              <Button 
                onClick={handleRetry}
                className="w-full bg-[#0F766E] hover:bg-[#115E59]"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Check Again
              </Button>
              <Button 
                variant="outline"
                onClick={() => window.location.href = '/'}
                className="w-full"
              >
                <Home className="h-4 w-4 mr-2" />
                Return Home
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Payment successful
  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 to-gray-100 py-12 px-4" data-testid="payment-success-page">
      {/* Header */}
      <div className="max-w-lg mx-auto mb-8 text-center">
        <div className="w-20 h-20 mx-auto mb-4 bg-[#0F766E] rounded-2xl flex items-center justify-center shadow-lg">
          <img src="/phs-logo.png" alt="PHS" className="w-16 h-16 object-contain" />
        </div>
        <h1 className="text-2xl font-bold text-gray-900">Park Home Support</h1>
      </div>

      <Card className="max-w-md mx-auto shadow-xl">
        <CardContent className="pt-8 text-center">
          {/* Success Icon */}
          <div className="w-24 h-24 mx-auto mb-6 bg-green-100 rounded-full flex items-center justify-center">
            <CheckCircle className="h-16 w-16 text-green-600" />
          </div>

          <h2 className="text-2xl font-bold text-gray-900 mb-2">Payment Successful!</h2>
          <p className="text-gray-600 mb-6">
            Thank you for your payment. Your work request is now ready for scheduling.
          </p>

          {/* Payment Details */}
          <div className="p-4 bg-gray-50 rounded-lg mb-6 text-left">
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-500">Amount Paid:</span>
                <span className="font-semibold text-[#0F766E]">
                  £{status?.amount?.toFixed(2) || '0.00'}
                </span>
              </div>
              {invoiceNumber && (
                <div className="flex justify-between">
                  <span className="text-gray-500">Invoice:</span>
                  <span className="font-mono text-sm">{invoiceNumber}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-gray-500">Status:</span>
                <span className="font-medium text-green-600">Confirmed</span>
              </div>
            </div>
          </div>

          {/* What Happens Next */}
          <div className="p-4 bg-[#0F766E]/10 rounded-lg mb-6 text-left border border-[#0F766E]/20">
            <h3 className="font-semibold text-[#0F766E] mb-2 flex items-center gap-2">
              <FileText className="h-4 w-4" />
              What Happens Next?
            </h3>
            <ol className="text-sm text-gray-600 space-y-1 list-decimal list-inside">
              <li>Your work request is now confirmed and ready for scheduling</li>
              <li>We will contact you to arrange a suitable appointment</li>
              <li>Our approved contractor will complete the work</li>
              <li>You'll receive confirmation once the job is complete</li>
            </ol>
          </div>

          <p className="text-sm text-gray-500 mb-6">
            A confirmation email has been sent to your registered email address.
          </p>

          <Button 
            onClick={() => window.location.href = '/'}
            className="w-full bg-[#0F766E] hover:bg-[#115E59]"
          >
            <Home className="h-4 w-4 mr-2" />
            Return to Home
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
